import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAppBridge } from '@shopify/app-bridge-react';
import { useI18n } from '@shopify/react-i18n';
import { useAuthFetch } from '../../hooks/useAuthFetch';
import { useCurrency } from '../../contexts/CurrencyContext';
import enTranslations from './translations/en.json';
import frTranslations from './translations/fr.json';
import TagHelpModal from '../../components/TagHelpModal';

const translationsByLocale = { en: enTranslations, fr: frTranslations };

export default function Feed() {
  const authFetch = useAuthFetch();
  const shopify = useAppBridge();
  const shop = shopify?.config?.shop ?? '';
  const [i18n] = useI18n({
    id: 'Feed',
    fallback: enTranslations,
    translations(locale) {
      return translationsByLocale[locale] ?? enTranslations;
    },
  });

  // Feed data
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);

  // Mode state
  const [feedFilterMode, setFeedFilterMode] = useState('TAG');
  const [manageMode, setManageMode] = useState(false);
  const [switchLoading, setSwitchLoading] = useState(false);
  const changeModeModalRef = useRef(null);
  const changeModeSelectRef = useRef(null);
  const closeModeModalBtnRef = useRef(null);

  // Manage mode state
  const [allProducts, setAllProducts] = useState([]);
  const [allProductsLoading, setAllProductsLoading] = useState(false);
  const [manageSearch, setManageSearch] = useState('');
  const [managePage, setManagePage] = useState(0);

  // Filters + sort + pagination (feed view only, client-side)
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('name-asc');
  const [currentPage, setCurrentPage] = useState(0);
  const PAGE_SIZE = 25;
  const currency = useCurrency();

  const loadProducts = useCallback(async () => {
    setSyncing(true);
    setError(null);
    try {
      const res = await authFetch('/api/products');
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setProducts(data.products);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setSyncing(false);
    }
  }, [authFetch]);

  const loadSettings = useCallback(async () => {
    try {
      const res = await authFetch('/api/settings');
      if (res.ok) {
        const data = await res.json();
        setFeedFilterMode(data.feedFilterMode || 'TAG');
      }
    } catch {
      // non-fatal — defaults to TAG
    }
  }, [authFetch]);

  useEffect(() => {
    loadSettings();
    loadProducts();
  }, [loadSettings, loadProducts]);

  // Reset feed pagination when search or sort changes
  useEffect(() => {
    setCurrentPage(0);
  }, [search, sort]);

  // Reset manage pagination when manage search changes
  useEffect(() => {
    setManagePage(0);
  }, [manageSearch]);

  // ── Feed view: filter + sort + paginate ────────────────────────────────
  const filteredProducts = useMemo(() => {
    const q = search.toLowerCase();

    const variantCount = {};
    const availableVariantCount = {};
    for (const { itemGroupId, availability } of products) {
      variantCount[itemGroupId] = (variantCount[itemGroupId] || 0) + 1;
      if (availability !== 'out of stock') {
        availableVariantCount[itemGroupId] = (availableVariantCount[itemGroupId] || 0) + 1;
      }
    }

    const seen = new Set();
    const deduped = products
      .filter(({ availability }) => availability !== 'out of stock')
      .filter(({ itemGroupId }) => {
        if (seen.has(itemGroupId)) return false;
        seen.add(itemGroupId);
        return true;
      })
      .map((p) => ({
        ...p,
        variantCount: variantCount[p.itemGroupId],
        availableVariantCount: availableVariantCount[p.itemGroupId],
      }));

    const filtered = deduped.filter(({ parentTitle, brand }) => {
      if (q && !parentTitle.toLowerCase().includes(q) && !brand.toLowerCase().includes(q)) return false;
      return true;
    });

    return [...filtered].sort((a, b) => {
      switch (sort) {
        case 'name-asc': return a.parentTitle.localeCompare(b.parentTitle);
        case 'name-desc': return b.parentTitle.localeCompare(a.parentTitle);
        case 'price-asc': return a.price - b.price;
        case 'price-desc': return b.price - a.price;
        case 'brand-asc': return a.brand.localeCompare(b.brand);
        default: return 0;
      }
    });
  }, [products, search, sort]);

  const totalFeedCount = useMemo(() => {
    const seen = new Set();
    products.filter(p => p.availability !== 'out of stock').forEach(p => seen.add(p.itemGroupId));
    return seen.size;
  }, [products]);

  const hasPreviousPage = currentPage > 0;
  const hasNextPage = (currentPage + 1) * PAGE_SIZE < filteredProducts.length;
  const pagedProducts = filteredProducts.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);

  // ── Manage mode: load all products ────────────────────────────────────
  const loadAllProducts = useCallback(async () => {
    setAllProductsLoading(true);
    try {
      const res = await authFetch('/api/all-products');
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setAllProducts(data.products);
    } catch (err) {
      setError(err.message);
    } finally {
      setAllProductsLoading(false);
    }
  }, [authFetch]);

  const enterManageMode = () => {
    setManageMode(true);
    setManageSearch('');
    setManagePage(0);
    loadAllProducts();
  };

  const exitManageMode = () => {
    setManageMode(false);
    loadProducts();
  };

  // ── Manage mode: filter + paginate ────────────────────────────────────
  const filteredAllProducts = useMemo(() => {
    const q = manageSearch.toLowerCase();
    if (!q) return allProducts;
    return allProducts.filter(
      ({ title, vendor }) =>
        title.toLowerCase().includes(q) || vendor.toLowerCase().includes(q)
    );
  }, [allProducts, manageSearch]);

  const pagedAllProducts = filteredAllProducts.slice(managePage * PAGE_SIZE, (managePage + 1) * PAGE_SIZE);
  const manageHasPrev = managePage > 0;
  const manageHasNext = (managePage + 1) * PAGE_SIZE < filteredAllProducts.length;
  const inFeedCount = allProducts.filter((p) => p.inFeed).length;  const pageAllInFeed = pagedAllProducts.length > 0 && pagedAllProducts.every(p => p.inFeed);
  const pageNoneInFeed = pagedAllProducts.every(p => !p.inFeed);

  const handleSelectAllPage = async () => {
    const shouldAdd = !pageAllInFeed;
    const targets = pagedAllProducts.filter(p => p.inFeed !== shouldAdd);
    if (targets.length === 0) return;

    const targetIds = new Set(targets.map(p => String(p.id)));
    setAllProducts(prev =>
      prev.map(p => targetIds.has(String(p.id)) ? { ...p, inFeed: shouldAdd } : p)
    );

    const results = await Promise.allSettled(
      targets.map(p =>
        authFetch(
          shouldAdd ? '/api/feed-products' : `/api/feed-products/${p.id}`,
          shouldAdd
            ? { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ productId: String(p.id) }) }
            : { method: 'DELETE' }
        )
      )
    );

    const failed = targets.filter((_, i) => results[i].status === 'rejected' || !results[i].value?.ok);
    if (failed.length > 0) {
      const failedIds = new Set(failed.map(p => String(p.id)));
      setAllProducts(prev =>
        prev.map(p => failedIds.has(String(p.id)) ? { ...p, inFeed: !shouldAdd } : p)
      );
      setError(i18n.translate('Feed.manage.selectAllError'));
    }
  };
  // ── Toggle product in/out of SERVERLIST ───────────────────────────────
  const handleToggleFeedProduct = async (productId, currentlyInFeed) => {
    // Optimistic update
    setAllProducts((prev) =>
      prev.map((p) => (String(p.id) === String(productId) ? { ...p, inFeed: !currentlyInFeed } : p))
    );
    try {
      const res = await authFetch(
        currentlyInFeed ? `/api/feed-products/${productId}` : '/api/feed-products',
        currentlyInFeed
          ? { method: 'DELETE' }
          : {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ productId: String(productId) }),
            }
      );
      if (!res.ok) throw new Error(await res.text());
    } catch (err) {
      // Revert on error
      setAllProducts((prev) =>
        prev.map((p) => (String(p.id) === String(productId) ? { ...p, inFeed: currentlyInFeed } : p))
      );
      setError(err.message);
    }
  };

  // ── Mode switching ─────────────────────────────────────────────────────
  const handleConfirmModeChange = async () => {
    const rawValue = changeModeSelectRef.current?.value;
    const newMode = rawValue ? rawValue.toUpperCase() : feedFilterMode;
    if (newMode === feedFilterMode) {
      closeModeModalBtnRef.current?.click();
      return;
    }
    setSwitchLoading(true);
    try {
      const res = await authFetch('/api/settings', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ feedFilterMode: newMode }),
      });
      if (!res.ok) throw new Error(await res.text());
      setFeedFilterMode(newMode);
      closeModeModalBtnRef.current?.click();
      loadProducts();
    } catch (err) {
      setError(err.message);
    } finally {
      setSwitchLoading(false);
    }
  };

  // ── Manage mode render ─────────────────────────────────────────────────
  if (manageMode) {
    return (
      <s-page heading={i18n.translate('Feed.manage.title')}>
        <s-box paddingBlock="base">
          <s-stack direction="inline" gap="small" alignItems="center" justifyContent="space-between">
            <s-stack direction="inline" gap="small" alignItems="center">
              <s-button icon="arrow-left" variant="tertiary" onClick={exitManageMode}>
                {i18n.translate('Feed.manage.back')}
              </s-button>
              <s-chip>{i18n.translate('Feed.manage.selectedCount', { count: inFeedCount })}</s-chip>
            </s-stack>
            <s-button variant="primary" onClick={exitManageMode}>
              {i18n.translate('Feed.manage.save')}
            </s-button>
          </s-stack>
        </s-box>

        <s-banner tone="info">
          {i18n.translate('Feed.manage.infoBanner')}
        </s-banner>

        {error && (
          <s-banner
            tone="critical"
            heading={error}
            dismissible
            onDismiss={() => setError(null)}
          />
        )}

        {allProductsLoading ? (
          <s-section>
            <s-stack direction="inline" justifyContent="center">
              <s-spinner />
            </s-stack>
          </s-section>
        ) : (
          <>
          <s-section padding="none">
            <s-table
              paginate={filteredAllProducts.length > PAGE_SIZE}
              hasPreviousPage={manageHasPrev}
              hasNextPage={manageHasNext}
              onNextpage={() => setManagePage((p) => p + 1)}
              onPreviouspage={() => setManagePage((p) => p - 1)}
            >
              <s-grid slot="filters" gap="small" gridTemplateColumns="1fr">
                <s-text-field
                  label={i18n.translate('Feed.filters.search')}
                  labelAccessibilityVisibility="exclusive"
                  icon="search"
                  placeholder={i18n.translate('Feed.filters.searchPlaceholder')}
                  value={manageSearch}
                  onChange={(e) => setManageSearch(e.currentTarget.value)}
                />
              </s-grid>

              <s-table-header-row>
                <s-table-header>
                  <s-checkbox
                    checked={pageAllInFeed}
                    indeterminate={!pageAllInFeed && !pageNoneInFeed}
                    onChange={handleSelectAllPage}
                    accessibilityLabel={i18n.translate('Feed.manage.columns.inFeed')}
                  />
                </s-table-header>
                <s-table-header listSlot="primary">
                  {i18n.translate('Feed.columns.product')}
                </s-table-header>
                <s-table-header listSlot="labeled">
                  {i18n.translate('Feed.columns.brand')}
                </s-table-header>
                <s-table-header format="currency" listSlot="labeled">
                  {i18n.translate('Feed.columns.price')}
                </s-table-header>
                <s-table-header>
                  {i18n.translate('Feed.columns.availability')}
                </s-table-header>
                <s-table-header>
                  {i18n.translate('Feed.columns.status')}
                </s-table-header>
              </s-table-header-row>

              <s-table-body>
                {pagedAllProducts.length === 0 ? (
                  <s-table-row>
                    <s-table-cell>
                      <s-text tone="subdued">{i18n.translate('Feed.filters.noResults')}</s-text>
                    </s-table-cell>
                    <s-table-cell /><s-table-cell /><s-table-cell /><s-table-cell /><s-table-cell />
                  </s-table-row>
                ) : (
                  pagedAllProducts.map(({ id, title, vendor, image, minPrice, inFeed, availability, status }) => (
                    <s-table-row key={id}>
                      <s-table-cell>
                        <s-checkbox
                          checked={inFeed}
                          onChange={() => handleToggleFeedProduct(id, inFeed)}
                        />
                      </s-table-cell>
                      <s-table-cell>
                        <s-stack direction="inline" gap="small" alignItems="center">
                          <s-thumbnail src={image || ''} alt={title} size="small" />
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '260px' }} title={title}>
                            <s-link href={`https://${shop}/admin/products/${id}`} target="_blank">
                              {title}
                            </s-link>
                          </div>
                        </s-stack>
                      </s-table-cell>
                      <s-table-cell>{vendor}</s-table-cell>
                      <s-table-cell>
                        {minPrice.toFixed(2)} {currency}
                      </s-table-cell>
                      <s-table-cell>
                        <s-badge tone={availability === 'in stock' ? 'success' : availability === 'preorder' ? 'warning' : 'critical'}>
                          {availability === 'in stock'
                            ? i18n.translate('Feed.columns.inStock')
                            : availability === 'preorder'
                              ? i18n.translate('Feed.columns.preorder')
                              : i18n.translate('Feed.columns.outOfStock')}
                        </s-badge>
                      </s-table-cell>
                      <s-table-cell>
                        <s-badge tone={status === 'active' ? 'success' : status === 'draft' ? 'info' : undefined}>
                          {status === 'active'
                            ? i18n.translate('Feed.columns.statusActive')
                            : status === 'draft'
                              ? i18n.translate('Feed.columns.statusDraft')
                              : i18n.translate('Feed.columns.statusArchived')}
                        </s-badge>
                      </s-table-cell>
                    </s-table-row>
                  ))
                )}
              </s-table-body>
            </s-table>
          </s-section>
          <s-box paddingBlock="base">
            <s-button variant="primary" onClick={exitManageMode}>
              {i18n.translate('Feed.manage.save')}
            </s-button>
          </s-box>
          </>
        )}
      </s-page>
    );
  }

  // ── Feed view render ───────────────────────────────────────────────────
  return (
    <s-page heading={i18n.translate('Feed.title')}>
      {/* ── Mode selector ─────────────────────────────────────────────────── */}
      <s-section heading={i18n.translate('Feed.modeSelect.label')}>
        <s-stack direction="block" gap="base">
          <s-text tone="subdued">
            {feedFilterMode === 'TAG'
              ? i18n.translate('Feed.modeSelect.tag')
              : i18n.translate('Feed.modeSelect.serverlist')}
          </s-text>
          <s-button variant="primary" commandFor="change-mode-modal" command="show-modal">
            {i18n.translate('Feed.modeSelect.changeButton')}
          </s-button>
        </s-stack>
      </s-section>

      {/* ── Modal ────────────────────────────────────────────────────────── */}
      <s-modal ref={changeModeModalRef} id="change-mode-modal" heading={i18n.translate('Feed.modeSelect.changeModalHeading')}>
        <s-box padding="base">
          <s-stack direction="block" gap="base">
            <s-select
              ref={changeModeSelectRef}
              label={i18n.translate('Feed.modeSelect.label')}
              value={feedFilterMode.toLowerCase()}
            >
              <s-option value="tag">{i18n.translate('Feed.modeSelect.tag')}</s-option>
              <s-option value="serverlist">{i18n.translate('Feed.modeSelect.serverlist')}</s-option>
            </s-select>
            <s-divider />
            <s-stack direction="block" gap="extra-small">
              <s-heading>{i18n.translate('Feed.modeSelect.helpModalOption1Title')}</s-heading>
              <s-text tone="subdued">
                {i18n.translate('Feed.modeSelect.helpModalOption1TextBefore')}{' '}
                <s-chip>marques-de-france</s-chip>{' '}
                {i18n.translate('Feed.modeSelect.helpModalOption1TextAfter')}
              </s-text>
            </s-stack>
            <s-stack direction="block" gap="extra-small">
              <s-heading>{i18n.translate('Feed.modeSelect.helpModalOption2Title')}</s-heading>
              <s-text tone="subdued">{i18n.translate('Feed.modeSelect.helpModalOption2Text')}</s-text>
            </s-stack>
            <s-stack direction="inline" gap="small">
              <s-button variant="primary" loading={switchLoading} onClick={handleConfirmModeChange}>
                {i18n.translate('Feed.modeSelect.saveButton')}
              </s-button>
              <s-button ref={closeModeModalBtnRef} variant="tertiary" commandFor="change-mode-modal" command="hide-modal">
                {i18n.translate('Feed.modeSelect.confirmCancel')}
              </s-button>
            </s-stack>
          </s-stack>
        </s-box>
      </s-modal>

      {/* ── TAG mode instructions section ──────────────────────────────────── */}
      {feedFilterMode === 'TAG' && (
        <>
          <s-section heading={i18n.translate('Feed.banner.title')}>
            <s-stack direction="block" gap="base">
              <s-text tone="subdued">
                {i18n.translate('Feed.banner.descriptionBefore')}{' '}
                <s-chip>marques-de-france</s-chip>{' '}
                {i18n.translate('Feed.banner.descriptionAfter')}
              </s-text>
              <s-stack direction="inline" gap="base" alignItems="center">
                <s-button
                  variant="primary"
                  href={`https://${shop}/admin/products`}
                  target="_blank"
                >
                  {i18n.translate('Feed.banner.action')}
                </s-button>
                <s-link
                  commandFor="tag-help-modal"
                  command="show-modal"
                >
                  {i18n.translate('Feed.banner.tagHelpLink')}
                </s-link>
              </s-stack>
            </s-stack>
          </s-section>
          <TagHelpModal id="tag-help-modal" />
        </>
      )}

      {error && (
        <s-banner
          tone="critical"
          heading={error}
          dismissible
          onDismiss={() => setError(null)}
        />
      )}

      {loading ? (
        <s-section>
          <s-stack direction="inline" justifyContent="center">
            <s-spinner />
          </s-stack>
        </s-section>
      ) : products.length === 0 ? (
        <s-section heading={i18n.translate(feedFilterMode === 'SERVERLIST' ? 'Feed.empty.titleServerlist' : 'Feed.empty.title')}>
          <s-stack direction="block" gap="base">
            {feedFilterMode === 'SERVERLIST' ? (
              <>
                <s-text tone="subdued">{i18n.translate('Feed.empty.descriptionServerlist')}</s-text>
                <s-button variant="primary" onClick={enterManageMode}>{i18n.translate('Feed.serverlist.banner.action')}</s-button>
              </>
            ) : (
              <>
                <s-stack direction="inline" gap="extra-small" alignItems="center">
                  <s-text tone="subdued">{i18n.translate('Feed.empty.descriptionBefore')}</s-text>
                  <s-chip>marques-de-france</s-chip>
                  <s-text tone="subdued">{i18n.translate('Feed.empty.descriptionAfter')}</s-text>
                </s-stack>
              </>
            )}
          </s-stack>
        </s-section>
      ) : (
        <>
          <s-banner tone="info">
            {i18n.translate('Feed.exclusionBanner')}
          </s-banner>
          <s-section padding="none">
            <s-table
              loading={syncing}
              paginate={filteredProducts.length > PAGE_SIZE}
              hasPreviousPage={hasPreviousPage}
              hasNextPage={hasNextPage}
              onNextpage={() => setCurrentPage((p) => p + 1)}
              onPreviouspage={() => setCurrentPage((p) => p - 1)}
            >
              <s-grid
                slot="filters"
                gap="small"
                gridTemplateColumns="1fr auto"
              >
                <s-text-field
                  label={i18n.translate('Feed.filters.search')}
                  labelAccessibilityVisibility="exclusive"
                  icon="search"
                  placeholder={i18n.translate('Feed.filters.searchPlaceholder')}
                  value={search}
                  onChange={(e) => setSearch(e.currentTarget.value)}
                />
                <s-stack direction="inline" gap="small">
                  <s-button
                    icon="sort"
                    variant="secondary"
                    accessibilityLabel={i18n.translate('Feed.sort.label')}
                    interestFor="feed-sort-tooltip"
                    commandFor="feed-sort-actions"
                  />
                  {feedFilterMode === 'SERVERLIST' && (
                    <s-button variant="primary" onClick={enterManageMode}>
                      {i18n.translate('Feed.serverlist.banner.editSelection')}
                    </s-button>
                  )}
                </s-stack>
                <s-tooltip id="feed-sort-tooltip">
                  <s-text>{i18n.translate('Feed.sort.label')}</s-text>
                </s-tooltip>
                <s-popover id="feed-sort-actions">
                  <s-box padding="small">
                    <s-stack direction="block" gap="small">
                      {[
                        { value: 'name-asc', label: i18n.translate('Feed.sort.nameAsc') },
                        { value: 'name-desc', label: i18n.translate('Feed.sort.nameDesc') },
                        { value: 'price-asc', label: i18n.translate('Feed.sort.priceAsc') },
                        { value: 'price-desc', label: i18n.translate('Feed.sort.priceDesc') },
                        { value: 'brand-asc', label: i18n.translate('Feed.sort.brandAsc') },
                      ].map(({ value, label }) => (
                        <s-button
                          key={value}
                          variant={sort === value ? 'primary' : 'tertiary'}
                          onClick={() => setSort(value)}
                        >
                          {label}
                        </s-button>
                      ))}
                    </s-stack>
                  </s-box>
                </s-popover>
              </s-grid>

              <s-table-header-row>
                <s-table-header listSlot="primary">
                  {i18n.translate('Feed.columns.product')}
                </s-table-header>
                <s-table-header listSlot="labeled">
                  {i18n.translate('Feed.columns.brand')}
                </s-table-header>
                <s-table-header format="currency" listSlot="labeled">
                  {i18n.translate('Feed.columns.price')}
                </s-table-header>
                {feedFilterMode === 'TAG' && (
                  <s-table-header>
                    {i18n.translate('Feed.columns.tags')}
                  </s-table-header>
                )}
                <s-table-header>
                  {i18n.translate('Feed.columns.availability')}
                </s-table-header>
                <s-table-header>
                  {i18n.translate('Feed.columns.status')}
                </s-table-header>
              </s-table-header-row>

              <s-table-body>
                {filteredProducts.length === 0 ? (
                  <s-table-row>
                    <s-table-cell>
                      <s-text tone="subdued">{i18n.translate('Feed.filters.noResults')}</s-text>
                    </s-table-cell>
                    <s-table-cell />
                    <s-table-cell />
                    <s-table-cell />
                  </s-table-row>
                ) : (
                  pagedProducts.map(({ itemGroupId, parentTitle, brand, price, image, availableVariantCount, tags, availability, status }) => (
                    <s-table-row key={itemGroupId}>
                      <s-table-cell>
                        <s-stack direction="inline" gap="small" alignItems="center">
                          <s-thumbnail src={image || ''} alt={parentTitle} size="small" />
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '260px' }} title={parentTitle}>
                            <s-link href={`https://${shop}/admin/products/${itemGroupId}`} target="_blank">
                              {parentTitle}
                            </s-link>
                          </div>
                          {availableVariantCount > 1 && (
                            <s-chip>{i18n.translate('Feed.columns.variants', { count: availableVariantCount })}</s-chip>
                          )}
                        </s-stack>
                      </s-table-cell>
                      <s-table-cell>{brand}</s-table-cell>
                      <s-table-cell>
                        {price.toFixed(2)} {currency}
                      </s-table-cell>
                      {feedFilterMode === 'TAG' && (
                        <s-table-cell>
                          {(tags || []).includes('marques-de-france') && (
                            <s-badge icon="check">marques-de-france</s-badge>
                          )}
                        </s-table-cell>
                      )}
                      <s-table-cell>
                        <s-badge tone={availability === 'in stock' ? 'success' : availability === 'preorder' ? 'warning' : 'critical'}>
                          {availability === 'in stock'
                            ? i18n.translate('Feed.columns.inStock')
                            : availability === 'preorder'
                              ? i18n.translate('Feed.columns.preorder')
                              : i18n.translate('Feed.columns.outOfStock')}
                        </s-badge>
                      </s-table-cell>
                      <s-table-cell>
                        <s-badge tone={status === 'active' ? 'success' : status === 'draft' ? 'info' : undefined}>
                          {status === 'active'
                            ? i18n.translate('Feed.columns.statusActive')
                            : status === 'draft'
                              ? i18n.translate('Feed.columns.statusDraft')
                              : i18n.translate('Feed.columns.statusArchived')}
                        </s-badge>
                      </s-table-cell>
                    </s-table-row>
                  ))
                )}
              </s-table-body>
            </s-table>
          </s-section>
        </>
      )}
    </s-page>
  );
}

